import { BackToTop, VStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { DrawerScreenProps } from '@react-navigation/drawer';
import { View } from 'native-base';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ScrollView, StyleSheet } from 'react-native';

import { TabSelector } from '../../components/TabSelector';
import { useRecoilQuery } from '../../hooks/useRecoilQuery';
import { CustomerDetailStackParamList } from '../../routes/types';
import { groupedCustomerDetailQuery } from '../../store/customerDetail/selector';
import { AdditionalAccountsProps } from '../../store/customerDetail/types';

import { ClientWithMismatchedSuitabilityModal } from './components/shared/ClientWithMismatchedSuitabilityModal';
import { Header } from './components/shared/Header';
import { InfoPanel } from './components/shared/InfoPanel';
import { CustomerDetailAvailableTabs, customerDetailsInitialTab, generateTabs } from './constants';
import { CustomerDetailProvider } from './context';

export declare namespace CustomerDetailTypes {
  interface Props {
    customerId: number;
    changeSelectedTab: (tab: CustomerDetailAvailableTabs, extraParams?: any) => void;
  }
}

const styles = StyleSheet.create({
  contentComponentStyle: { flex: 1 },
});

export const CustomerDetail: React.FC<
  DrawerScreenProps<CustomerDetailStackParamList, 'CustomerDetail'>
> = ({ route, navigation }) => {
  const customerDetailTabsAvailable = generateTabs();
  const { customerId, accountNumber, selectedTab: selectTabFromParams } = route.params;
  const scrollViewRef = useRef<ScrollView>(null);
  const isMobile = useBreakpointValue({
    base: true,
    sm: false,
  });

  const [accountSelected, setAccountSelected] = useState<AdditionalAccountsProps>();

  const selectedTab = selectTabFromParams ?? customerDetailsInitialTab;

  const setSelectedTab = useMemo(() => {
    return (tab: CustomerDetailAvailableTabs, extraParams?: any) => {
      navigation.setParams({ selectedTab: tab, ...extraParams });
    };
  }, [navigation]);

  const { data, loading, error, refresher } = useRecoilQuery(
    groupedCustomerDetailQuery({ accountNumber })
  );

  const handleGoBack = () => {
    navigation.navigate('AppDrawer', {
      screen: 'Customers',
    });
  };

  const handleChangeAccount = (sinacorAccount: string) => {
    const account = data?.additionalAccounts.find(
      (acc) => acc.sinacorAccountNormalized === sinacorAccount
    );
    if (account) {
      navigation.setParams({
        accountId: account.accountId,
        accountNumber: account.accountNumber,
      });
    }
  };

  useEffect(() => {
    if (data?.additionalAccounts.length) {
      setAccountSelected(data.additionalAccounts[0]);
    }
  }, [data]);

  const ContentComponent = customerDetailTabsAvailable[selectedTab].component;

  useEffect(() => {
    if (data?.personalInformation.fullName) {
      navigation.setOptions({
        title: `${data.personalInformation.fullName} - Detalhes do cliente`,
      });
    }
  }, [data?.personalInformation.fullName, navigation]);

  return (
    <ScrollView
      ref={scrollViewRef}
      style={styles.contentComponentStyle}
      showsHorizontalScrollIndicator={false}>
      <CustomerDetailProvider
        isLoading={loading}
        hasError={!!error}
        onRefresh={refresher}
        customer={data}
        accountSelected={accountSelected}
        handleChangeAccount={handleChangeAccount}>
        <View
          p={Spacing['inset.xs']}
          flex={1}
          width="full"
          maxWidth={{ base: '100%', sm: '95%', lg: '1280px' }}
          minWidth={0}>
          <VStack space={Spacing['stack.xxs']} flex={1} width="full">
            <Header isMobile={isMobile} onGoBack={handleGoBack} />
            <TabSelector<typeof customerDetailTabsAvailable>
              selectedTab={selectedTab}
              setSelectedTab={(tab) => setSelectedTab(tab as CustomerDetailAvailableTabs)}
              tabList={customerDetailTabsAvailable}
              isMobile={isMobile}
            />
            {!isMobile && <InfoPanel />}
            <ContentComponent customerId={customerId} changeSelectedTab={setSelectedTab} />
            <BackToTop scrollViewRef={scrollViewRef} />
            <ClientWithMismatchedSuitabilityModal accountNumber={accountNumber} />
          </VStack>
        </View>
      </CustomerDetailProvider>
    </ScrollView>
  );
};
